/**
 * Threat Tempo indicator — replaces the fake DEFCON badge with a
 * data-driven directional indicator showing whether the situation
 * is escalating, steady, or calming.
 *
 * Derived from three real signals already computed by the analysis engine:
 *   1. Event velocity (rate vs baseline, trend direction)
 *   2. Surge count (active surges = something abnormal is happening)
 *   3. Severity shift (% of critical+high events trending up or down)
 *
 * Display: ▲ ESCALATING (red) | ► ELEVATED (amber) | ► STEADY (cyan) | ▼ CALMING (green)
 */

import { eventBus } from '@/core/events';
import { appStore } from '@/core/store';
import type { AnalysisResult, VelocityMetrics } from '@/intelligence/analysis/types';

type TempoLevel = 'escalating' | 'elevated' | 'steady' | 'calming';

interface TempoState {
  level: TempoLevel;
  arrow: string;
  label: string;
  color: string;
  detail: string;
}

const TEMPO_MAP: Record<TempoLevel, Omit<TempoState, 'detail'>> = {
  escalating: { level: 'escalating', arrow: '\u25B2', label: 'ESCALATING', color: '#ef4444' },
  elevated:   { level: 'elevated',   arrow: '\u25B6', label: 'ELEVATED',   color: '#f59e0b' },
  steady:     { level: 'steady',     arrow: '\u25B6', label: 'STEADY',     color: '#06b6d4' },
  calming:    { level: 'calming',    arrow: '\u25BC', label: 'CALMING',    color: '#22c55e' },
};

/** Compute severity ratio: fraction of critical+high events in last 6 hours. */
function computeSeverityRatio(events: { severity?: string; timestamp: string }[]): {
  current: number;
  previous: number;
} {
  const now = Date.now();
  const threeHoursAgo = now - 3 * 60 * 60 * 1000;
  const sixHoursAgo = now - 6 * 60 * 60 * 1000;

  let currentHigh = 0, currentTotal = 0;
  let previousHigh = 0, previousTotal = 0;

  for (const ev of events) {
    const ts = new Date(ev.timestamp).getTime();
    if (Number.isNaN(ts)) continue;

    const isHigh = ev.severity === 'critical' || ev.severity === 'high';

    if (ts >= threeHoursAgo) {
      currentTotal++;
      if (isHigh) currentHigh++;
    } else if (ts >= sixHoursAgo) {
      previousTotal++;
      if (isHigh) previousHigh++;
    }
  }

  return {
    current: currentTotal > 0 ? currentHigh / currentTotal : 0,
    previous: previousTotal > 0 ? previousHigh / previousTotal : 0,
  };
}

/**
 * Synthesize tempo from velocity, surge count, and severity shift.
 * Returns a weighted score: positive = escalating, negative = calming.
 */
function computeTempo(
  velocity: VelocityMetrics | null,
  surgeCount: number,
  severityRatio: { current: number; previous: number },
): TempoLevel {
  let score = 0;

  // Velocity contribution (weight: 40%)
  if (velocity) {
    const { level, trend } = velocity.global;
    if (level === 'spike') score += 2;
    else if (level === 'elevated') score += 1;

    if (trend === 'rising') score += 1.5;
    else if (trend === 'falling') score -= 1.5;
  }

  // Surge contribution (weight: 30%)
  if (surgeCount >= 3) score += 2;
  else if (surgeCount >= 1) score += 1;

  // Severity shift contribution (weight: 30%)
  const severityDelta = severityRatio.current - severityRatio.previous;
  if (severityDelta > 0.15) score += 2;
  else if (severityDelta > 0.05) score += 1;
  else if (severityDelta < -0.15) score -= 2;
  else if (severityDelta < -0.05) score -= 1;

  // Classify
  if (score >= 3) return 'escalating';
  if (score >= 1.5) return 'elevated';
  if (score <= -1.5) return 'calming';
  return 'steady';
}

// Module-level state for cleanup
let activeUnsubs: (() => void)[] = [];

export function createThreatTempo(): HTMLElement {
  // Clean up previous subscriptions (HMR safety)
  for (const unsub of activeUnsubs) unsub();
  activeUnsubs = [];

  const pill = document.createElement('div');
  pill.className = 'tempo-badge';
  pill.id = 'tempo-badge';

  const arrowSpan = document.createElement('span');
  arrowSpan.className = 'tempo-arrow';
  pill.appendChild(arrowSpan);

  const labelSpan = document.createElement('span');
  labelSpan.className = 'tempo-label';
  pill.appendChild(labelSpan);

  let lastVelocity: VelocityMetrics | null = null;
  let lastSurgeCount = 0;
  let lastLevel: TempoLevel | null = null;

  function render(): void {
    const events = appStore.getState().events;
    const severityRatio = computeSeverityRatio(events);
    const level = computeTempo(lastVelocity, lastSurgeCount, severityRatio);

    // Skip DOM update if unchanged
    if (level === lastLevel) return;
    lastLevel = level;

    const style = TEMPO_MAP[level];
    arrowSpan.textContent = style.arrow;
    labelSpan.textContent = style.label;

    pill.style.color = style.color;
    pill.style.borderColor = style.color;

    // Build tooltip detail
    const rateInfo = lastVelocity ? `${lastVelocity.global.rate}/hr (${lastVelocity.global.trend})` : 'warming up';
    const surgeInfo = lastSurgeCount > 0 ? `${lastSurgeCount} active` : 'none';
    const sevInfo = `${Math.round(severityRatio.current * 100)}% high/crit`;
    pill.title = `Threat Tempo: ${style.label}\nEvent rate: ${rateInfo}\nSurges: ${surgeInfo}\nSeverity: ${sevInfo}`;

    // Flash animation on level change
    if (lastLevel !== null) {
      pill.classList.remove('tempo-flash');
      void pill.offsetWidth;
      pill.classList.add('tempo-flash');
    }
  }

  // Initial render (will show STEADY until data arrives)
  render();

  // Subscribe to analysis results (contains velocity + surges)
  const unsubAnalysis = eventBus.on('analysis:complete', (result: AnalysisResult) => {
    lastVelocity = result.velocity;
    lastSurgeCount = result.surges.length;
    render();
  });
  activeUnsubs.push(unsubAnalysis);

  // Also subscribe to velocity events directly (more frequent)
  const unsubVelocity = eventBus.on('analysis:velocity', (metrics: VelocityMetrics) => {
    lastVelocity = metrics;
    render();
  });
  activeUnsubs.push(unsubVelocity);

  // Recompute on new event batches (severity ratio changes)
  const unsubEvents = appStore.subscribe(
    (s) => s.events.length,
    () => render(),
  );
  activeUnsubs.push(unsubEvents);

  // Attach destroy for teardown
  (pill as HTMLElement & { destroy?: () => void }).destroy = () => {
    for (const unsub of activeUnsubs) unsub();
    activeUnsubs = [];
  };

  return pill;
}
